import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  author: string;
  publishDate: string;
  readTime: string;
  imageUrl: string;
  variant?: 'default' | 'featured' | 'horizontal';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  category,
  categorySlug,
  author,
  publishDate,
  readTime,
  imageUrl,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer"
      >
        <div className="aspect-[16/9] w-full overflow-hidden rounded-xl">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent rounded-xl"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="inline-block px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-3">
            {category}
          </span>
          <h3 className="font-heading font-bold text-lg md:text-xl text-background-50 mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
            {title}
          </h3>
          <p className="text-sm text-background-200/80 line-clamp-2 mb-3">{excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-background-300">
            <span>{author}</span>
            <span>·</span>
            <span>{publishDate}</span>
            <span>·</span>
            <span>{readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium w-fit mb-2">
            {category}
          </span>
          <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1.5 line-clamp-2 text-base">
            {title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{excerpt}</p>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span>{author}</span>
            <span>·</span>
            <span>{publishDate}</span>
            <span>·</span>
            <span>{readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="aspect-[4/3] w-full overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5">
        <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-2.5">
          {category}
        </span>
        <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2 text-sm md:text-base">
          {title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{excerpt}</p>
        <div className="flex items-center gap-2 text-xs text-foreground-400">
          <span>{author}</span>
          <span>·</span>
          <span>{publishDate}</span>
          <span>·</span>
          <span>{readTime}</span>
        </div>
      </div>
    </Link>
  );
}